import { useState, useCallback, useRef } from 'react';
export function useMultiSelect(orderedKeys) {
    const [selectedKeys, setSelectedKeys] = useState(new Set());
    const lastClickedRef = useRef(null);
    // Always-current ref so toggle reads the latest ordered keys without
    // needing orderedKeys in its dependency array (avoids stale closures).
    const orderedKeysRef = useRef(orderedKeys);
    orderedKeysRef.current = orderedKeys;
    const isSelected = useCallback((key) => selectedKeys.has(key), [selectedKeys]);
    const toggle = useCallback((key, shiftKey) => {
        setSelectedKeys(prev => {
            const next = new Set(prev);
            const currentOrderedKeys = orderedKeysRef.current;
            if (shiftKey && lastClickedRef.current) {
                // Range select
                const lastIndex = currentOrderedKeys.indexOf(lastClickedRef.current);
                const currentIndex = currentOrderedKeys.indexOf(key);
                if (lastIndex !== -1 && currentIndex !== -1) {
                    const start = Math.min(lastIndex, currentIndex);
                    const end = Math.max(lastIndex, currentIndex);
                    for (let i = start; i <= end; i++) {
                        const k = currentOrderedKeys[i];
                        if (k)
                            next.add(k);
                    }
                }
                else {
                    // Fallback: just toggle the single key
                    if (next.has(key))
                        next.delete(key);
                    else
                        next.add(key);
                }
            }
            else {
                // Single toggle
                if (next.has(key))
                    next.delete(key);
                else
                    next.add(key);
            }
            lastClickedRef.current = key;
            return next;
        });
    }, []);
    const selectAll = useCallback((allKeys) => {
        setSelectedKeys(new Set(allKeys));
    }, []);
    const clearSelection = useCallback(() => {
        setSelectedKeys(new Set());
        lastClickedRef.current = null;
    }, []);
    return {
        selectedKeys,
        isSelected,
        toggle,
        selectAll,
        clearSelection,
        selectionCount: selectedKeys.size,
    };
}
//# sourceMappingURL=useMultiSelect.js.map